import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import {
  FaStar,
  FaStarHalfAlt,
  FaRegStar,
  FaArrowRight,
  FaSpinner,
  FaHeart,
  FaEye,
  FaTrophy,
  FaMedal,
  FaAward,
  FaQuoteLeft,
  FaThumbsUp,
  FaComment,
  FaUserCircle,
  FaFire,
  FaCrown,
  FaStarOfLife,
  FaGrinStars,
  FaCalculator,
  FaChartLine,
  FaSearch,
  FaComments,
  FaGlobe,
  FaHistory,
  FaUniversity,
  FaFileAlt,
  FaDownload,
  FaUsers,
  FaBook,
  FaRocket,
  FaBrain,
} from "react-icons/fa";
import { MdSchool, MdLibraryBooks } from "react-icons/md";
import {
  FiBook,
  FiDownload,
  FiUsers,
  FiStar,
  FiTrendingUp,
  FiAward,
  FiBarChart2,
  FiUploadCloud,
} from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi";
import axios from "axios";
import { buildApiUrl } from "../utils/apiConfig";

const Home = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth) || {};

  const [stats, setStats] = useState({
    totalNotes: 0,
    totalDownloads: 0,
    totalUsers: 0,
    averageRating: 0,
    platformRating: 0,
    totalRatings: 0,
  });
  const [topContributors, setTopContributors] = useState([]);
  const [topRatedNotes, setTopRatedNotes] = useState([]);
  const [platformReviews, setPlatformReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRating, setUserRating] = useState(0);
  const [userHover, setUserHover] = useState(0);
  const [userReview, setUserReview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [imageError, setImageError] = useState({});
  const [toastShown, setToastShown] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchTopContributors();
    fetchTopRatedNotes();
    fetchPlatformReviews();
    fetchPlatformRatings();
    checkDarkMode();
  }, []);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get("/api/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchPlatformRatings = async () => {
    try {
      const response = await axios.get(
        "/api/ratings/platform",
      );
      setStats((prev) => ({
        ...prev,
        platformRating: response.data.averageRating || 0,
        totalRatings: response.data.totalRatings || 0,
      }));
    } catch (error) {
      console.error("Failed to fetch platform ratings:", error);
    }
  };

  const fetchPlatformReviews = async () => {
    try {
      const response = await axios.get(
        "/api/ratings/platform",
      );
      setPlatformReviews(response.data.ratings || []);
    } catch (error) {
      console.error("Failed to fetch platform reviews:", error);
      setPlatformReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopContributors = async () => {
    try {
      const response = await axios.get(
        "/api/users/top-contributors",
      );
      setTopContributors(response.data || []);
    } catch (error) {
      console.error("Failed to fetch top contributors:", error);
    }
  };

  const fetchTopRatedNotes = async () => {
    try {
      const response = await axios.get(
        "/api/notes?limit=20",
      );
      const notes = response.data.notes || [];
      const ratedNotes = notes
        .filter((note) => note.rating > 0)
        .sort((a, b) => b.rating - a.rating)
        .slice(0, 5);
      setTopRatedNotes(ratedNotes);
    } catch (error) {
      console.error("Failed to fetch top rated notes:", error);
    }
  };

  const handleSubmitRating = async () => {
    if (!isAuthenticated) {
      toast.error("Please login to rate the platform");
      navigate("/login");
      return;
    }

    if (userRating === 0) {
      toast.error("Please select a rating");
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "/api/ratings",
        { rating: userRating, review: userReview, category: "platform" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Thank you for your feedback! ❤️");
      setUserRating(0);
      setUserReview("");
      fetchPlatformRatings();
      fetchPlatformReviews();
      fetchStats();
    } catch (error) {
      console.error("Rating error:", error);
      toast.error("Failed to submit rating. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatNumber = (num) => {
    if (num === 0) return "0";
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M+";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K+";
    return num + "+";
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} color="#f59e0b" size={14} />);
    }
    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" color="#f59e0b" size={14} />);
    }
    while (stars.length < 5) {
      stars.push(<FaRegStar key={stars.length} color="#f59e0b" size={14} />);
    }
    return stars;
  };

  const renderLargeStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} color="#f59e0b" size={28} />);
    }
    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" color="#f59e0b" size={28} />);
    }
    while (stars.length < 5) {
      stars.push(<FaRegStar key={stars.length} color="#f59e0b" size={28} />);
    }
    return stars;
  };

  const renderMediumStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} color="#f59e0b" size={16} />);
    }
    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" color="#f59e0b" size={16} />);
    }
    while (stars.length < 5) {
      stars.push(<FaRegStar key={stars.length} color="#f59e0b" size={16} />);
    }
    return stars;
  };

  const statItems = [
    {
      icon: <FiBook size={32} strokeWidth={1.5} />,
      value: formatNumber(stats.totalNotes),
      label: "Study Materials",
      description: "Comprehensive notes & resources",
      gradient: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
    },
    {
      icon: <FiDownload size={32} strokeWidth={1.5} />,
      value: formatNumber(stats.totalDownloads),
      label: "Resource Downloads",
      description: "Knowledge shared globally",
      gradient: "linear-gradient(135deg, #10b981, #059669)",
    },
    {
      icon: <FiUsers size={32} strokeWidth={1.5} />,
      value: formatNumber(stats.totalUsers),
      label: "Active Students",
      description: "Growing community of learners",
      gradient: "linear-gradient(135deg, #06b6d4, #0891b2)",
    },
    {
      icon: <FiStar size={32} strokeWidth={1.5} />,
      value: stats.platformRating > 0 ? stats.platformRating.toFixed(1) : "0",
      label: "Platform Rating",
      description: "Student satisfaction",
      gradient: "linear-gradient(135deg, #f59e0b, #d97706)",
    },
  ];

  const features = [
    {
      icon: <MdLibraryBooks size={40} />,
      title: "Comprehensive Notes",
      description:
        "Access notes for all semesters and branches with detailed explanations",
      link: "/notes",
      requiresAuth: true,
      gradient: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
    },
    {
      icon: <FaCalculator size={40} />,
      title: "SGPA Calculator",
      description: "Calculate your SGPA accurately with VTU grading system",
      link: "/sgpa-calculator",
      requiresAuth: true,
      gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
    },
    {
      icon: <FaChartLine size={40} />,
      title: "Result Comparison",
      description: "Compare results across multiple students and semesters",
      link: "/dashboard",
      requiresAuth: true,
      gradient: "linear-gradient(135deg, #06b6d4, #0891b2)",
    },
    {
      icon: <FaSearch size={40} />,
      title: "Check Results",
      description: "Instant Access to the Latest VTU Examination Results",
      link: "/results",
      requiresAuth: true,
      gradient: "linear-gradient(135deg, #f59e0b, #d97706)",
    },
    {
      icon: <FaComments size={40} />,
      title: "Community Chat",
      description: "Connect and discuss with fellow VTU students",
      link: "/chat",
      requiresAuth: true,
      gradient: "linear-gradient(135deg, #ec4899, #be185d)",
    },
    {
      icon: <FaHistory size={40} />,
      title: "Recent Results",
      description: "Quick access to recent exam results",
      link: "/recent-results",
      requiresAuth: true,
      gradient: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
    },
  ];

  const values = [
    {
      icon: <FiTrendingUp size={28} strokeWidth={1.5} />,
      title: "Quality First",
      description: "Curated and verified content",
      gradient: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
    },
    {
      icon: <FiUsers size={28} strokeWidth={1.5} />,
      title: "Community Driven",
      description: "Built by students, for students",
      gradient: "linear-gradient(135deg, #06b6d4, #0891b2)",
    },
    {
      icon: <FiAward size={28} strokeWidth={1.5} />,
      title: "Free Access",
      description: "Education should be free",
      gradient: "linear-gradient(135deg, #10b981, #059669)",
    },
  ];

  const branches = [
    { code: "CSE", name: "Computer Science Engineering", color: "#8b5cf6" },
    { code: "ISE", name: "Information Science Engineering", color: "#3b82f6" },
    { code: "ECE", name: "Electronics & Communication", color: "#06b6d4" },
    { code: "EEE", name: "Electrical & Electronics", color: "#f59e0b" },
    { code: "MECH", name: "Mechanical Engineering", color: "#10b981" },
    { code: "CIVIL", name: "Civil Engineering", color: "#ef4444" },
    { code: "AIML", name: "AI & Machine Learning", color: "#ec4899" },
    { code: "DS", name: "Data Science", color: "#8b5cf6" },
  ];

  const getRankDetails = (index) => {
    if (index === 0)
      return {
        icon: <FaCrown size={32} />,
        title: "Rank #1",
        gradient: "linear-gradient(135deg, #f59e0b, #d97706)",
        bgGradient: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
      };
    if (index === 1)
      return {
        icon: <FaMedal size={32} />,
        title: "Rank #2",
        gradient: "linear-gradient(135deg, #94a3b8, #64748b)",
        bgGradient: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
      };
    if (index === 2)
      return {
        icon: <FaAward size={32} />,
        title: "Rank #3",
        gradient: "linear-gradient(135deg, #cd7f32, #b87333)",
        bgGradient: "linear-gradient(135deg, #2e1065 0%, #1e1b4b 100%)",
      };
    return {
      icon: <FaStarOfLife size={28} />,
      title: `Rank #${index + 1}`,
      gradient: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
      bgGradient: "linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)",
    };
  };

  const handleImageError = (key) => {
    setImageError((prev) => ({ ...prev, [key]: true }));
  };

  const getProfileImage = (user) => {
    const profilePicture = user?.profilePicture;
    const name = user?.name;

    if (!profilePicture || imageError[profilePicture]) {
      const initials =
        name
          ?.split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2) || "U";

      return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%238b5cf6'/%3E%3Ctext x='50' y='65' font-size='40' text-anchor='middle' fill='white' font-family='Arial'%3E${initials}%3C/text%3E%3C/svg%3E`;
    }
    if (profilePicture.startsWith("http")) {
      return profilePicture;
    }
    if (profilePicture.startsWith("data:")) {
      return profilePicture;
    }
    return buildApiUrl(profilePicture);
  };

  const handleFeatureClick = (feature, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (feature.requiresAuth && !isAuthenticated) {
      if (!toastShown) {
        setToastShown(true);
        toast.error(`Please login to access ${feature.title}`);
        setTimeout(() => setToastShown(false), 2000);
      }
      navigate("/login");
      return;
    }
    navigate(feature.link);
  };

  const handleBranchClick = (branchCode) => {
    if (!isAuthenticated) {
      if (!toastShown) {
        setToastShown(true);
        toast.error("Please login to access study materials");
        setTimeout(() => setToastShown(false), 2000);
      }
      navigate("/login");
      return;
    }
    navigate(`/notes?branch=${branchCode}`);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const statCardVariants = {
    hidden: { scale: 0.9, opacity: 0, y: 20 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 400, damping: 25 },
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: { type: "spring", stiffness: 400, damping: 17 },
    },
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: "1rem",
          background: isDarkMode ? "#0f0f1a" : "#f8fafc",
        }}
      >
        <FaSpinner
          style={{
            animation: "spin 1s linear infinite",
            fontSize: "3rem",
            color: "#8b5cf6",
          }}
        />
        <p style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
          Loading amazing content...
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: isDarkMode ? "#0f0f1a" : "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* Animated Background Particles */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.3, 0] }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              delay: Math.random() * 10,
            }}
            style={{
              position: "absolute",
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: Math.random() * 3 + 1,
              height: Math.random() * 3 + 1,
              background: `linear-gradient(135deg, #8b5cf6, #3b82f6)`,
              borderRadius: "50%",
              filter: "blur(2px)",
            }}
          />
        ))}
      </div>

      {/* Hero Section - Modern Gradient */}
      <section
        style={{
          background:
            "linear-gradient(135deg, #1e1b4b 0%, #2e1065 50%, #1e1b4b 100%)",
          color: "white",
          padding: "6rem 2rem",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.1, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity }}
          style={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            background:
              "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
            borderRadius: "50%",
          }}
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.1, 0.2],
          }}
          transition={{ duration: 10, repeat: Infinity, delay: 2 }}
          style={{
            position: "absolute",
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            background:
              "radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 70%)",
            borderRadius: "50%",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 300 }}
          style={{
            maxWidth: "900px",
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            style={{ marginBottom: "1rem" }}
          >
            <HiOutlineSparkles
              size={48}
              color="#8b5cf6"
              style={{ margin: "0 auto" }}
            />
          </motion.div>
          <h1
            style={{
              fontSize: "3.5rem",
              marginBottom: "1rem",
              fontWeight: "bold",
              background: "linear-gradient(135deg, #fff, #c4b5fd)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Welcome to VTUGenX
          </h1>
          <p
            style={{
              fontSize: "1.2rem",
              marginBottom: "2rem",
              opacity: 0.9,
              color: "#cbd5e1",
            }}
          >
            Your Ultimate Destination for VTU Study Materials, Results, SGPA
            Calculator, and Community Discussion
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/notes"
                style={{
                  padding: "1rem 2rem",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  textDecoration: "none",
                  borderRadius: "40px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  transition: "all 0.3s",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
                onClick={(e) => {
                  if (!isAuthenticated) {
                    e.preventDefault();
                    if (!toastShown) {
                      setToastShown(true);
                      toast.error("Please login to explore notes");
                      setTimeout(() => setToastShown(false), 2000);
                    }
                    navigate("/login");
                  }
                }}
              >
                Explore Notes <FaArrowRight />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/results"
                style={{
                  padding: "1rem 2rem",
                  background: "rgba(255,255,255,0.1)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  textDecoration: "none",
                  borderRadius: "40px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  border: "1px solid rgba(139,92,246,0.5)",
                  transition: "all 0.3s",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
                onClick={(e) => {
                  if (!isAuthenticated) {
                    e.preventDefault();
                    if (!toastShown) {
                      setToastShown(true);
                      toast.error("Please login to check results");
                      setTimeout(() => setToastShown(false), 2000);
                    }
                    navigate("/login");
                  }
                }}
              >
                Check Results <FaArrowRight />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/register"
                style={{
                  padding: "1rem 2rem",
                  background: "transparent",
                  color: "white",
                  textDecoration: "none",
                  borderRadius: "40px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  border: "2px solid white",
                  transition: "all 0.3s",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "white";
                  e.target.style.color = "#8b5cf6";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.color = "white";
                }}
              >
                Join Now <FaArrowRight />
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Stats Section - Glassmorphism */}
      <section
        style={{ padding: "5rem 2rem", position: "relative", zIndex: 1 }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: "center", marginBottom: "3rem" }}
          >
            <div
              style={{
                display: "inline-block",
                background: "rgba(139,92,246,0.1)",
                padding: "0.3rem 1rem",
                borderRadius: "50px",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  color: "#8b5cf6",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                }}
              >
                PLATFORM STATISTICS
              </span>
            </div>
            <h2
              style={{
                fontSize: "2.5rem",
                color: isDarkMode ? "#f1f5f9" : "#1e293b",
                marginBottom: "0.5rem",
              }}
            >
              Our Impact in <span style={{ color: "#8b5cf6" }}>Numbers</span>
            </h2>
            <p
              style={{
                color: isDarkMode ? "#94a3b8" : "#64748b",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              Real data from our growing community
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "2rem",
            }}
          >
            {statItems.map((stat, index) => (
              <motion.div
                key={index}
                variants={statCardVariants}
                whileHover="hover"
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  background: isDarkMode
                    ? "rgba(30, 30, 45, 0.8)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "24px",
                  boxShadow: isDarkMode
                    ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                    : "0 25px 50px -12px rgba(0,0,0,0.25)",
                  transition: "transform 0.3s",
                  position: "relative",
                  overflow: "hidden",
                  border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background: stat.gradient,
                  }}
                />
                <div
                  style={{
                    background: stat.gradient,
                    width: "70px",
                    height: "70px",
                    borderRadius: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1rem auto",
                    color: "white",
                  }}
                >
                  {stat.icon}
                </div>
                <h3
                  style={{
                    fontSize: "2rem",
                    color: isDarkMode ? "#f1f5f9" : "#1e293b",
                    marginBottom: "0.3rem",
                    fontWeight: "bold",
                  }}
                >
                  {stat.value}
                </h3>
                <p
                  style={{
                    color: isDarkMode ? "#f1f5f9" : "#1e293b",
                    fontWeight: "600",
                    marginBottom: "0.3rem",
                  }}
                >
                  {stat.label}
                </p>
                <p
                  style={{
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    fontSize: "0.85rem",
                  }}
                >
                  {stat.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section
        style={{
          padding: "5rem 2rem",
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: "center", marginBottom: "3rem" }}
        >
          <div
            style={{
              display: "inline-block",
              background: "rgba(139,92,246,0.1)",
              padding: "0.3rem 1rem",
              borderRadius: "50px",
              marginBottom: "1rem",
            }}
          >
            <span
              style={{
                color: "#8b5cf6",
                fontSize: "0.85rem",
                fontWeight: "500",
              }}
            >
              EVERYTHING YOU NEED
            </span>
          </div>
          <h2
            style={{
              fontSize: "2.5rem",
              color: isDarkMode ? "#f1f5f9" : "#1e293b",
              marginBottom: "0.5rem",
            }}
          >
            All <span style={{ color: "#8b5cf6" }}>Tools</span> for VTU Students
          </h2>
          <p
            style={{
              color: isDarkMode ? "#94a3b8" : "#64748b",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            From study materials to result checking, we've got you covered
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "2rem",
          }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -10 }}
              style={{
                padding: "2rem",
                textAlign: "center",
                background: isDarkMode
                  ? "rgba(30, 30, 45, 0.8)"
                  : "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(20px)",
                borderRadius: "24px",
                boxShadow: isDarkMode
                  ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                  : "0 25px 50px -12px rgba(0,0,0,0.25)",
                transition: "all 0.3s",
                cursor: "pointer",
                border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
              }}
              onClick={(e) => handleFeatureClick(feature, e)}
            >
              <div
                style={{
                  background: feature.gradient,
                  width: "80px",
                  height: "80px",
                  borderRadius: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1.5rem auto",
                  color: "white",
                }}
              >
                {feature.icon}
              </div>
              <h3
                style={{
                  color: isDarkMode ? "#f1f5f9" : "#1e293b",
                  marginBottom: "1rem",
                  fontWeight: "bold",
                }}
              >
                {feature.title}
              </h3>
              <p
                style={{
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                  lineHeight: "1.6",
                }}
              >
                {feature.description}
              </p>
              {feature.requiresAuth && !isAuthenticated && (
                <div
                  style={{
                    marginTop: "1rem",
                    padding: "0.3rem 0.8rem",
                    background: isDarkMode
                      ? "rgba(139,92,246,0.2)"
                      : "rgba(139,92,246,0.1)",
                    borderRadius: "20px",
                    display: "inline-block",
                    fontSize: "0.7rem",
                    color: "#8b5cf6",
                  }}
                >
                  Login Required
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Values Section - Glassmorphism */}
      <section
        style={{
          padding: "5rem 2rem",
          position: "relative",
          zIndex: 1,
          background: isDarkMode ? "rgba(0,0,0,0.2)" : "rgba(139,92,246,0.03)",
        }}
      >
        <div
          style={{ maxWidth: "1200px", margin: "0 auto", textAlign: "center" }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div
              style={{
                display: "inline-block",
                background: "rgba(139,92,246,0.1)",
                padding: "0.3rem 1rem",
                borderRadius: "50px",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  color: "#8b5cf6",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                }}
              >
                CORE VALUES
              </span>
            </div>
            <h2
              style={{
                fontSize: "2.5rem",
                color: isDarkMode ? "#f1f5f9" : "#1e293b",
                marginBottom: "1rem",
              }}
            >
              What Drives <span style={{ color: "#8b5cf6" }}>Us</span>
            </h2>
            <p
              style={{
                color: isDarkMode ? "#94a3b8" : "#64748b",
                marginBottom: "3rem",
                maxWidth: "600px",
                margin: "0 auto 3rem auto",
              }}
            >
              Our principles guide everything we do at VTUGenX
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "2rem",
            }}
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -5 }}
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  background: isDarkMode
                    ? "rgba(30, 30, 45, 0.8)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "24px",
                  boxShadow: isDarkMode
                    ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                    : "0 25px 50px -12px rgba(0,0,0,0.25)",
                  border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    background: value.gradient,
                    borderRadius: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1.5rem auto",
                    color: "white",
                  }}
                >
                  {value.icon}
                </div>
                <h3
                  style={{
                    color: isDarkMode ? "#f1f5f9" : "#1e293b",
                    marginBottom: "0.5rem",
                    fontWeight: "bold",
                  }}
                >
                  {value.title}
                </h3>
                <p
                  style={{
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    lineHeight: "1.6",
                  }}
                >
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Top Contributors Section - Premium Dark Gradient */}
      {topContributors.length > 0 && (
        <section
          style={{
            padding: "5rem 2rem",
            background: "linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
              position: "relative",
              zIndex: 1,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{ textAlign: "center", marginBottom: "4rem" }}
            >
              <div
                style={{
                  display: "inline-block",
                  background: "rgba(139,92,246,0.2)",
                  padding: "0.5rem 1.5rem",
                  borderRadius: "50px",
                  marginBottom: "1rem",
                }}
              >
                <FaFire style={{ color: "#f59e0b", marginRight: "8px" }} />
                <span style={{ color: "#f59e0b", fontWeight: "bold" }}>
                  LEADERBOARD
                </span>
              </div>
              <h2
                style={{
                  fontSize: "2.8rem",
                  color: "white",
                  marginBottom: "0.5rem",
                }}
              >
                Top <span style={{ color: "#8b5cf6" }}>Contributors</span>
              </h2>
              <p style={{ color: "#94a3b8", fontSize: "1.1rem" }}>
                Our amazing community members who make a difference
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              style={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "2rem",
              }}
            >
              {topContributors.map((user, index) => {
                const rank = getRankDetails(index);
                return (
                  <motion.div
                    key={user._id}
                    variants={itemVariants}
                    whileHover={{ y: -15, scale: 1.05 }}
                    style={{
                      textAlign: "center",
                      padding: "2rem 1.5rem",
                      background: rank.bgGradient,
                      borderRadius: "30px",
                      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
                      width: "260px",
                      position: "relative",
                      transition: "all 0.3s ease",
                      border: `1px solid rgba(139,92,246,0.3)`,
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "-20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: rank.gradient,
                        padding: "0.5rem 1rem",
                        borderRadius: "50px",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
                      }}
                    >
                      {rank.icon}
                      <span
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: "bold",
                          color: "#fff",
                        }}
                      >
                        {rank.title}
                      </span>
                    </div>

                    <div
                      style={{
                        marginTop: "35px",
                        marginBottom: "15px",
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "110px",
                          height: "110px",
                          borderRadius: "50%",
                          background: `radial-gradient(circle, ${rank.gradient.split(",")[0]}, transparent)`,
                          animation: "pulse 2s infinite",
                        }}
                      />
                      <img
                        src={getProfileImage(user)}
                        alt={user.name}
                        style={{
                          width: "100px",
                          height: "100px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: `3px solid ${rank.gradient.split(",")[0]}`,
                          boxShadow: `0 8px 25px rgba(0,0,0,0.3)`,
                          position: "relative",
                          zIndex: 1,
                          backgroundColor: "#f0f0f0",
                        }}
                        onError={() => handleImageError(user.profilePicture)}
                      />
                    </div>

                    <h3
                      style={{
                        color: "white",
                        marginBottom: "0.5rem",
                        fontSize: "1.3rem",
                        fontWeight: "bold",
                      }}
                    >
                      {user.name}
                    </h3>

                    <div
                      style={{
                        background: "rgba(139,92,246,0.2)",
                        borderRadius: "15px",
                        padding: "0.8rem",
                        marginBottom: "1rem",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "2rem",
                          fontWeight: "bold",
                          color: "#8b5cf6",
                        }}
                      >
                        {user.contributions?.notesUploaded || 0}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
                        Notes Uploaded
                      </div>
                    </div>

                    <p
                      style={{
                        color: "#94a3b8",
                        fontSize: "0.85rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      <FaUserCircle /> {user.branch || "Student"}
                    </p>

                    <div
                      style={{
                        marginTop: "1rem",
                        width: "100%",
                        background: "rgba(255,255,255,0.1)",
                        borderRadius: "10px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(100, ((user.contributions?.notesUploaded || 0) / (topContributors[0]?.contributions?.notesUploaded || 1)) * 100)}%`,
                          height: "4px",
                          background: rank.gradient,
                          transition: "width 0.5s ease",
                        }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              style={{ textAlign: "center", marginTop: "3rem" }}
            >
              <p style={{ color: "#94a3b8" }}>
                <FaFire
                  style={{
                    color: "#f59e0b",
                    display: "inline",
                    marginRight: "5px",
                  }}
                />
                Be the next star! Share notes and climb the leaderboard
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Top Rated Notes Section */}
      {topRatedNotes.length > 0 && (
        <section
          style={{
            padding: "5rem 2rem",
            maxWidth: "1200px",
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: "center", marginBottom: "3rem" }}
          >
            <div
              style={{
                display: "inline-block",
                background: "rgba(139,92,246,0.1)",
                padding: "0.3rem 1rem",
                borderRadius: "50px",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  color: "#8b5cf6",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                }}
              >
                TOP RATED
              </span>
            </div>
            <h2
              style={{
                fontSize: "2.5rem",
                color: isDarkMode ? "#f1f5f9" : "#1e293b",
                marginBottom: "0.5rem",
              }}
            >
              Highest Rated{" "}
              <span style={{ color: "#8b5cf6" }}>Study Materials</span>
            </h2>
            <p
              style={{
                color: isDarkMode ? "#94a3b8" : "#64748b",
                fontSize: "1.1rem",
              }}
            >
              Quality content, loved by students
            </p>
          </motion.div>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: "2rem",
            }}
          >
            {topRatedNotes.map((note, idx) => (
              <motion.div
                key={note._id}
                variants={itemVariants}
                whileHover={{ y: -5 }}
              >
                <Link
                  to={`/notes/${note._id}`}
                  style={{ textDecoration: "none" }}
                  onClick={(e) => {
                    if (!isAuthenticated) {
                      e.preventDefault();
                      if (!toastShown) {
                        setToastShown(true);
                        toast.error("Please login to view notes");
                        setTimeout(() => setToastShown(false), 2000);
                      }
                      navigate("/login");
                    }
                  }}
                >
                  <div
                    style={{
                      background: isDarkMode
                        ? "rgba(30, 30, 45, 0.8)"
                        : "rgba(255, 255, 255, 0.9)",
                      backdropFilter: "blur(20px)",
                      borderRadius: "20px",
                      overflow: "hidden",
                      boxShadow: isDarkMode
                        ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                        : "0 25px 50px -12px rgba(0,0,0,0.25)",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                    }}
                  >
                    <div
                      style={{
                        padding: "1rem",
                        background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                        color: "white",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>
                        {note.subjectCode}
                      </span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.3rem",
                          background: "rgba(255,255,255,0.2)",
                          padding: "0.2rem 0.8rem",
                          borderRadius: "20px",
                        }}
                      >
                        {renderStars(note.rating)}
                        <span
                          style={{ marginLeft: "0.5rem", fontWeight: "bold" }}
                        >
                          {note.rating}
                        </span>
                      </div>
                    </div>
                    <div style={{ padding: "1.5rem", flex: 1 }}>
                      <h3
                        style={{
                          color: isDarkMode ? "#f1f5f9" : "#1e293b",
                          marginBottom: "0.5rem",
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                        }}
                      >
                        {note.title}
                      </h3>
                      <p
                        style={{
                          color: isDarkMode ? "#94a3b8" : "#64748b",
                          fontSize: "0.9rem",
                          lineHeight: "1.5",
                          marginBottom: "1rem",
                        }}
                      >
                        {note.description?.substring(0, 100)}...
                      </p>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: "auto",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "1rem",
                            color: isDarkMode ? "#94a3b8" : "#64748b",
                            fontSize: "0.8rem",
                          }}
                        >
                          <span>
                            <FaEye /> {note.views || 0}
                          </span>
                          <span>
                            <FaDownload /> {note.downloads || 0}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            color: "#ef4444",
                          }}
                        >
                          <FaHeart /> {note.likes?.length || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </section>
      )}

      {/* Branches Section */}
      <section
        style={{
          padding: "5rem 2rem",
          background: isDarkMode ? "rgba(0,0,0,0.2)" : "rgba(139,92,246,0.03)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: "center", marginBottom: "3rem" }}
          >
            <div
              style={{
                display: "inline-block",
                background: "rgba(139,92,246,0.1)",
                padding: "0.3rem 1rem",
                borderRadius: "50px",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  color: "#8b5cf6",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                }}
              >
                ACADEMIC PROGRAMS
              </span>
            </div>
            <h2
              style={{
                textAlign: "center",
                fontSize: "2.5rem",
                color: isDarkMode ? "#f1f5f9" : "#1e293b",
                marginBottom: "0.5rem",
              }}
            >
              Available <span style={{ color: "#8b5cf6" }}>Branches</span>
            </h2>
            <p
              style={{
                color: isDarkMode ? "#94a3b8" : "#64748b",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              Comprehensive coverage for all VTU engineering disciplines
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {branches.map((branch, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.03, y: -5 }}
                style={{
                  background: isDarkMode
                    ? "rgba(30, 30, 45, 0.8)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "20px",
                  padding: "1.5rem",
                  textAlign: "center",
                  cursor: "pointer",
                  border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                  transition: "all 0.3s ease",
                  boxShadow: isDarkMode
                    ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                    : "0 25px 50px -12px rgba(0,0,0,0.25)",
                }}
                onClick={() => handleBranchClick(branch.code)}
              >
                <div
                  style={{
                    width: "70px",
                    height: "70px",
                    background: `${branch.color}20`,
                    borderRadius: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1rem auto",
                  }}
                >
                  <span
                    style={{
                      fontSize: "2rem",
                      fontWeight: "bold",
                      color: branch.color,
                    }}
                  >
                    {branch.code.charAt(0)}
                  </span>
                </div>
                <h3
                  style={{
                    fontSize: "1.2rem",
                    color: branch.color,
                    marginBottom: "0.5rem",
                    fontWeight: "bold",
                  }}
                >
                  {branch.code}
                </h3>
                <p
                  style={{
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    fontSize: "0.9rem",
                    lineHeight: "1.4",
                  }}
                >
                  {branch.name}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Platform Reviews Section */}
      {platformReviews.length > 0 && (
        <section
          style={{
            padding: "5rem 2rem",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{ textAlign: "center", marginBottom: "3rem" }}
            >
              <div
                style={{
                  display: "inline-block",
                  background: "rgba(139,92,246,0.1)",
                  padding: "0.3rem 1rem",
                  borderRadius: "50px",
                  marginBottom: "1rem",
                }}
              >
                <span
                  style={{
                    color: "#8b5cf6",
                    fontSize: "0.85rem",
                    fontWeight: "500",
                  }}
                >
                  STUDENT FEEDBACK
                </span>
              </div>
              <h2
                style={{
                  fontSize: "2.5rem",
                  color: isDarkMode ? "#f1f5f9" : "#1e293b",
                  marginBottom: "0.5rem",
                }}
              >
                What <span style={{ color: "#8b5cf6" }}>Students Say</span>
              </h2>
              <p
                style={{
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                  fontSize: "1.1rem",
                }}
              >
                Real 5-star reviews from our community
              </p>
            </motion.div>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              style={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "2rem",
              }}
            >
              {platformReviews.map((review, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -5, scale: 1.02 }}
                  style={{
                    background: isDarkMode
                      ? "rgba(30, 30, 45, 0.8)"
                      : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    padding: "1.5rem",
                    borderRadius: "24px",
                    boxShadow: isDarkMode
                      ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                      : "0 25px 50px -12px rgba(0,0,0,0.25)",
                    width: "320px",
                    position: "relative",
                    transition: "all 0.3s ease",
                    border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                  }}
                >
                  <FaQuoteLeft
                    style={{
                      position: "absolute",
                      top: "20px",
                      right: "20px",
                      color: "#8b5cf6",
                      opacity: 0.2,
                      fontSize: "2rem",
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      marginBottom: "1rem",
                    }}
                  >
                    <img
                      src={
                        review.user?.profilePicture ||
                        `https://ui-avatars.com/api/?name=${review.user?.name}&background=8b5cf6&color=fff&size=50&rounded=true`
                      }
                      alt={review.user?.name}
                      style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid #8b5cf6",
                      }}
                    />
                    <div>
                      <h4
                        style={{
                          color: isDarkMode ? "#f1f5f9" : "#1e293b",
                          marginBottom: "0.2rem",
                          fontWeight: "bold",
                          fontSize: "1rem",
                        }}
                      >
                        {review.user?.name}
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.3rem",
                        }}
                      >
                        {renderMediumStars(review.rating)}
                        <span
                          style={{
                            fontSize: "0.75rem",
                            color: isDarkMode ? "#94a3b8" : "#64748b",
                            marginLeft: "0.3rem",
                          }}
                        >
                          ({review.rating}/5)
                        </span>
                      </div>
                    </div>
                  </div>

                  <p
                    style={{
                      color: isDarkMode ? "#cbd5e1" : "#475569",
                      lineHeight: "1.6",
                      marginBottom: "1rem",
                      fontSize: "0.9rem",
                      display: "-webkit-box",
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    "{review.review}"
                  </p>

                  <div
                    style={{
                      marginTop: "0.8rem",
                      paddingTop: "0.8rem",
                      borderTop: `1px solid ${isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                    >
                      <FaThumbsUp color="#8b5cf6" size={12} />
                      <span
                        style={{
                          color: isDarkMode ? "#94a3b8" : "#64748b",
                          fontSize: "0.7rem",
                        }}
                      >
                        Verified Student
                      </span>
                    </div>
                    <span
                      style={{
                        color: isDarkMode ? "#94a3b8" : "#64748b",
                        fontSize: "0.7rem",
                      }}
                    >
                      {new Date(review.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Rate Us Section - Glassmorphism */}
      <section
        style={{
          padding: "5rem 2rem",
          background: isDarkMode
            ? "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)"
            : "linear-gradient(135deg, #f3e8ff 0%, #e0e7ff 100%)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{ maxWidth: "600px", margin: "0 auto", textAlign: "center" }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              background: isDarkMode
                ? "rgba(30, 30, 45, 0.9)"
                : "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(20px)",
              borderRadius: "32px",
              padding: "2.5rem",
              boxShadow: isDarkMode
                ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                : "0 25px 50px -12px rgba(0,0,0,0.25)",
              border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #f59e0b, #d97706)",
                width: "80px",
                height: "80px",
                borderRadius: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1.5rem auto",
              }}
            >
              <FaGrinStars size={40} color="white" />
            </div>
            <h2
              style={{
                fontSize: "2rem",
                color: isDarkMode ? "#f1f5f9" : "#1e293b",
                marginBottom: "0.5rem",
              }}
            >
              Rate <span style={{ color: "#8b5cf6" }}>VTUGenX</span>
            </h2>
            <p
              style={{
                color: isDarkMode ? "#94a3b8" : "#64748b",
                marginBottom: "1.5rem",
              }}
            >
              Your feedback helps us improve and serve you better
            </p>

            <div
              style={{
                marginBottom: "2rem",
                padding: "1rem",
                background: isDarkMode
                  ? "rgba(0,0,0,0.3)"
                  : "rgba(139,92,246,0.05)",
                borderRadius: "20px",
              }}
            >
              <p
                style={{
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                  marginBottom: "0.5rem",
                }}
              >
                Current Rating
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                <div>{renderLargeStars(stats.platformRating)}</div>
                <span
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    color: "#f59e0b",
                  }}
                >
                  {stats.platformRating.toFixed(1)}
                </span>
                <span style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                  ({stats.totalRatings} ratings)
                </span>
              </div>
            </div>

            <div style={{ marginBottom: "1.5rem" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "1rem",
                  flexWrap: "wrap",
                }}
              >
                {(() => {
                  const stars = [];
                  for (let i = 1; i <= 5; i++) {
                    stars.push(
                      <motion.div
                        key={i}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                        style={{ display: "inline-block" }}
                      >
                        <FaStar
                          size={45}
                          color={
                            i <= (userHover || userRating)
                              ? "#f59e0b"
                              : "#e4e5e9"
                          }
                          style={{
                            cursor: "pointer",
                            margin: "0 5px",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={() => setUserHover(i)}
                          onMouseLeave={() => setUserHover(userRating)}
                          onClick={() => setUserRating(i)}
                        />
                      </motion.div>,
                    );
                  }
                  return stars;
                })()}
              </div>
              <p
                style={{
                  color: "#f59e0b",
                  fontSize: "1rem",
                  fontWeight: "500",
                }}
              >
                {userRating === 1 && "Poor - Needs major improvement"}
                {userRating === 2 && "Fair - Could be better"}
                {userRating === 3 && "Good - Satisfactory"}
                {userRating === 4 && "Very Good - Happy with the platform"}
                {userRating === 5 && "Excellent - Love it! ❤️"}
                {userRating === 0 && "Click a star to rate"}
              </p>
            </div>

            <textarea
              value={userReview}
              onChange={(e) => setUserReview(e.target.value)}
              placeholder="Share your experience with VTUGenX (optional)..."
              rows="3"
              style={{
                width: "100%",
                padding: "1rem",
                border: `2px solid ${isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)"}`,
                borderRadius: "16px",
                outline: "none",
                fontSize: "1rem",
                resize: "vertical",
                marginBottom: "1.5rem",
                fontFamily: "inherit",
                background: isDarkMode ? "rgba(0,0,0,0.3)" : "white",
                color: isDarkMode ? "#f1f5f9" : "#1e293b",
              }}
            />

            <motion.button
              whileHover={{ scale: userRating === 0 ? 1 : 1.02 }}
              whileTap={{ scale: userRating === 0 ? 1 : 0.98 }}
              onClick={handleSubmitRating}
              disabled={submitting || userRating === 0}
              style={{
                width: "100%",
                padding: "1rem",
                background:
                  userRating === 0
                    ? "#94a3b8"
                    : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                color: "white",
                border: "none",
                borderRadius: "16px",
                fontSize: "1.1rem",
                fontWeight: "bold",
                cursor: userRating === 0 ? "not-allowed" : "pointer",
                transition: "all 0.3s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              <FaHeart /> {submitting ? "Submitting..." : "Submit Your Rating"}
            </motion.button>

            {!isAuthenticated && (
              <p
                style={{
                  marginTop: "1rem",
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                  fontSize: "0.9rem",
                }}
              >
                <Link to="/login" style={{ color: "#8b5cf6" }}>
                  Login
                </Link>{" "}
                to rate the platform
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        style={{
          padding: "5rem 2rem",
          background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
          color: "white",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.05, 0.1],
          }}
          transition={{ duration: 10, repeat: Infinity }}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 600,
            height: 600,
            background:
              "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
            borderRadius: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ position: "relative", zIndex: 1 }}
        >
          <FaRocket
            size={60}
            color="#8b5cf6"
            style={{ marginBottom: "1rem" }}
          />
          <h2
            style={{
              fontSize: "2.3rem",
              marginBottom: "1rem",
              fontWeight: "600",
              letterSpacing: "0.5px",
              background: "linear-gradient(135deg, #fff, #c4b5fd)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Ready to Ace Your Exams?
          </h2>
          <p
            style={{
              fontSize: "1.2rem",
              marginBottom: "2rem",
              opacity: 0.9,
              color: "#cbd5e1",
            }}
          >
            Join {formatNumber(stats.totalUsers)}+ students who are already
            using VTUGenX for notes, results, and SGPA calculation
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/register"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "1rem 2.5rem",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  textDecoration: "none",
                  borderRadius: "50px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  transition: "all 0.3s",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                }}
              >
                Get Started Now <FaArrowRight />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/dashboard"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "1rem 2.5rem",
                  background: "rgba(255,255,255,0.1)",
                  backdropFilter: "blur(10px)",
                  color: "white",
                  textDecoration: "none",
                  borderRadius: "50px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  border: "1px solid rgba(139,92,246,0.5)",
                  transition: "all 0.3s",
                }}
              >
                Go to Dashboard <FaArrowRight />
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
          50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.3; }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
        }
        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(20px, 20px) rotate(10deg); }
        }
      `}</style>
    </div>
  );
};

export default Home;

